import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PatientEncounterNote } from '@/types/patient-chart-api';

/**
 * Patient notes list — Chart > Notes tab (PROD-738 PR C).
 *
 * Wire (Flutter `patient_chart_data_source_impl.dart:1117`):
 *
 *   GET /api/Patients/GetPatientEncounterNotes
 *       ?PatientId={id}&PageNumber={n}&PageSize={n}
 *       → { patientEncounterList: PatientEncounterNote[] }
 *
 * The "Notes" tab actually lists ENCOUNTERS (visits), not freestanding
 * note records — Flutter naming quirk. The SOAP body of each encounter
 * lives on the encounter detail screen (encounter epic surface, not
 * the patient chart). PR C is read-only listing only.
 *
 * **Wire wrapper**: response is `{ patientEncounterList: [...] }`,
 * NOT a flat array. Unwrap at the queryFn boundary so consumers
 * see a clean `PatientEncounterNote[]`.
 *
 * Server-side paginated. Default pageSize 20 — matches Flutter's
 * default. Heuristic pager (`hasMore = page.length === pageSize`)
 * since the wire ships no totalCount.
 */

interface PatientNotesResponse {
  patientEncounterList?: PatientEncounterNote[] | null;
}

const DEFAULT_PAGE_SIZE = 20;

export interface UsePatientNotesParams {
  patientId: string | null | undefined;
  pageNumber?: number;
  pageSize?: number;
}

export function usePatientNotes(params: UsePatientNotesParams) {
  return useQuery({
    queryKey: [
      'patient-notes',
      params.patientId,
      params.pageNumber ?? 1,
      params.pageSize ?? DEFAULT_PAGE_SIZE,
    ],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({
        PatientId: params.patientId ?? '',
        PageNumber: String(params.pageNumber ?? 1),
        PageSize: String(params.pageSize ?? DEFAULT_PAGE_SIZE),
      });
      const raw = await get<PatientNotesResponse>(
        `/api/Patients/GetPatientEncounterNotes?${qs.toString()}`,
        { signal },
      );
      return raw?.patientEncounterList ?? [];
    },
    enabled: !!params.patientId,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}
